import React, { useState, useEffect } from 'react';
import { Image, Plus, Edit, Trash2, X, Save, Loader2, Eye, EyeOff } from 'lucide-react';
import { getAdminPortfolio, updatePortfolio, createPortfolio, deletePortfolio } from '../../utils/adminApi';
import { useAdmin } from '../../contexts/AdminContext';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';

const PortfolioManagement = () => {
  const { token } = useAdmin();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: { en: '', es: '', de: '' },
    description: { en: '', es: '', de: '' },
    category: { en: '', es: '', de: '' },
    bg_color: '#D4AF37',
    active: true
  });

  useEffect(() => {
    loadPortfolio();
  }, []);

  const loadPortfolio = async () => {
    try {
      setLoading(true);
      const data = await getAdminPortfolio();
      setProjects(data.projects || []);
    } catch (error) {
      console.error('Error loading portfolio:', error);
      toast.error('Failed to load portfolio');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: { en: '', es: '', de: '' },
      description: { en: '', es: '', de: '' },
      category: { en: '', es: '', de: '' },
      bg_color: '#D4AF37',
      active: true
    });
    setEditingProject(null);
  };

  const openEditModal = (project) => {
    setEditingProject(project);
    setFormData({
      name: project.name || { en: '', es: '', de: '' },
      description: project.description || { en: '', es: '', de: '' },
      category: project.category || { en: '', es: '', de: '' },
      bg_color: project.bg_color || '#D4AF37',
      active: project.active !== false
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!formData.name.en) {
      toast.error('Name (English) is required');
      return;
    }

    setSaving(true);
    try {
      if (editingProject) {
        await updatePortfolio(token, editingProject.project_id, formData);
        toast.success('Project updated successfully');
      } else {
        await createPortfolio(token, formData);
        toast.success('Project created successfully');
      }
      setShowModal(false);
      resetForm();
      loadPortfolio();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save project');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (projectId, projectName) => {
    if (!window.confirm(`Are you sure you want to delete "${projectName}"?`)) return;
    
    try {
      await deletePortfolio(token, projectId);
      toast.success('Project deleted');
      loadPortfolio();
    } catch (error) {
      toast.error('Failed to delete project');
    }
  };

  const handleToggleActive = async (project) => {
    try {
      await updatePortfolio(token, project.project_id, { active: !project.active });
      toast.success(`Project ${project.active ? 'hidden' : 'visible'}`);
      loadPortfolio();
    } catch (error) {
      toast.error('Failed to update project');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-[#D4AF37] animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="portfolio-management">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <Image className="w-7 h-7 text-[#D4AF37]" />
            Portfolio Management
          </h1>
          <p className="text-[#9CA3AF] mt-1">Manage your portfolio projects</p>
        </div>
        <Button
          onClick={() => { resetForm(); setShowModal(true); }}
          className="bg-[#D4AF37] hover:bg-[#c9a432] text-[#141619]"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Project
        </Button>
      </div>

      {/* Projects Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {projects.map((project) => (
          <div
            key={project.project_id}
            className="rounded-xl border border-[#2a2d32] overflow-hidden"
            style={{ backgroundColor: `${project.bg_color}20` }}
          >
            <div 
              className="h-3"
              style={{ backgroundColor: project.bg_color }}
            />
            <div className="p-6">
              <div className="flex justify-between items-start mb-3">
                <h3 className="text-lg font-bold text-white">{project.name?.en || 'Untitled'}</h3>
                <span className={`px-2 py-1 rounded text-xs ${
                  project.active !== false ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                }`}>
                  {project.active !== false ? 'Visible' : 'Hidden'}
                </span>
              </div>
              
              <span className="text-sm text-[#D4AF37] mb-2 block">{project.category?.en}</span>
              
              <p className="text-[#9CA3AF] text-sm mb-4 line-clamp-2">
                {project.description?.en || 'No description'}
              </p>
              
              <div className="flex items-center gap-2 pt-4 border-t border-[#2a2d32]/50">
                <button
                  onClick={() => openEditModal(project)}
                  className="flex-1 flex items-center justify-center gap-2 py-2 bg-[#1a1d21] hover:bg-[#2a2d32] rounded-lg transition-colors text-white text-sm"
                >
                  <Edit className="w-4 h-4" />
                  Edit
                </button>
                <button
                  onClick={() => handleToggleActive(project)}
                  className="p-2 hover:bg-[#1a1d21] rounded-lg transition-colors"
                >
                  {project.active !== false ? (
                    <EyeOff className="w-4 h-4 text-[#9CA3AF]" />
                  ) : (
                    <Eye className="w-4 h-4 text-[#9CA3AF]" />
                  )}
                </button>
                <button
                  onClick={() => handleDelete(project.project_id, project.name?.en)}
                  className="p-2 hover:bg-red-500/10 rounded-lg transition-colors"
                >
                  <Trash2 className="w-4 h-4 text-red-400" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-[#1a1d21] rounded-xl border border-[#2a2d32] w-full max-w-2xl max-h-[90vh] overflow-auto">
            <div className="flex items-center justify-between p-6 border-b border-[#2a2d32]">
              <h2 className="text-xl font-bold text-white">
                {editingProject ? 'Edit Project' : 'Create New Project'}
              </h2>
              <button onClick={() => { setShowModal(false); resetForm(); }} className="p-2 hover:bg-[#2a2d32] rounded-lg">
                <X className="w-5 h-5 text-[#9CA3AF]" />
              </button>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Name Fields */}
              <div>
                <h3 className="text-sm font-medium text-[#D4AF37] mb-3">Project Name</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {['en', 'es', 'de'].map(lang => (
                    <div key={lang}>
                      <label className="block text-xs text-[#6B7280] mb-1">{lang.toUpperCase()}</label>
                      <Input
                        value={formData.name[lang]}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          name: { ...prev.name, [lang]: e.target.value }
                        }))}
                        className="bg-[#141619] border-[#2a2d32] text-white"
                      />
                    </div>
                  ))}
                </div>
              </div>

              {/* Category Fields */}
              <div>
                <h3 className="text-sm font-medium text-[#D4AF37] mb-3">Category</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {['en', 'es', 'de'].map(lang => (
                    <div key={lang}>
                      <label className="block text-xs text-[#6B7280] mb-1">{lang.toUpperCase()}</label>
                      <Input
                        value={formData.category[lang]}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          category: { ...prev.category, [lang]: e.target.value }
                        }))}
                        className="bg-[#141619] border-[#2a2d32] text-white"
                        placeholder={lang === 'en' ? 'Web Design' : lang === 'es' ? 'Diseño Web' : 'Webdesign'}
                      />
                    </div>
                  ))}
                </div>
              </div>

              {/* Description */}
              <div>
                <h3 className="text-sm font-medium text-[#D4AF37] mb-3">Description</h3>
                {['en', 'es', 'de'].map(lang => (
                  <div key={lang} className="mb-3">
                    <label className="block text-xs text-[#6B7280] mb-1">{lang.toUpperCase()}</label>
                    <textarea
                      value={formData.description[lang]}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        description: { ...prev.description, [lang]: e.target.value }
                      }))}
                      className="w-full px-3 py-2 bg-[#141619] border border-[#2a2d32] rounded-lg text-white"
                      rows={2}
                    />
                  </div>
                ))}
              </div>

              {/* Color & Active */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#9CA3AF] mb-2">Brand Color</label>
                  <div className="flex items-center gap-3">
                    <input
                      type="color"
                      value={formData.bg_color}
                      onChange={(e) => setFormData(prev => ({ ...prev, bg_color: e.target.value }))}
                      className="w-12 h-10 rounded cursor-pointer"
                    />
                    <Input
                      value={formData.bg_color}
                      onChange={(e) => setFormData(prev => ({ ...prev, bg_color: e.target.value }))}
                      className="bg-[#141619] border-[#2a2d32] text-white font-mono"
                    />
                  </div>
                </div>
                <div className="flex items-center pt-8">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.active}
                      onChange={(e) => setFormData(prev => ({ ...prev, active: e.target.checked }))}
                      className="w-4 h-4 rounded border-[#2a2d32] bg-[#141619] text-[#D4AF37]"
                    />
                    <span className="text-sm text-[#9CA3AF]">Visible on website</span>
                  </label>
                </div>
              </div>
            </div>

            <div className="flex gap-3 p-6 border-t border-[#2a2d32]">
              <Button variant="outline" onClick={() => { setShowModal(false); resetForm(); }} className="flex-1 border-[#2a2d32] text-white hover:bg-[#2a2d32]">
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={saving} className="flex-1 bg-[#D4AF37] hover:bg-[#c9a432] text-[#141619]">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                {editingProject ? 'Update' : 'Create'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PortfolioManagement;
